<template>
  <div class="dev-tips-manager p-6">
    <!-- Header Section -->
    <div class="header-section mb-6">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            Development Tips Manager
            <span v-if="$route.query.dimension" class="text-lg font-normal text-purple-600">
              - {{ $route.query.dimension }}
            </span>
          </h1>
          <p class="text-gray-600">
            Manage development tips for assessment dimensions
            <span v-if="$route.query.dimension" class="text-purple-600 font-medium">
              (Filtered by dimension: {{ $route.query.dimension }})
            </span>
          </p>
        </div>
        <div class="flex flex-col sm:flex-row gap-2">
          <Button
            label="Create Tip"
            icon="pi pi-plus"
            class="p-button-primary"
            @click="openCreateDialog"
          />
          <Button
            label="Bulk Import"
            icon="pi pi-upload"
            class="p-button-secondary"
            @click="openBulkImportDialog"
          />
          <Button
            label="Load All Tips"
            icon="pi pi-refresh"
            class="p-button-outlined p-button-info"
            @click="handleLoadAllTips"
            :loading="loading"
          />
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="statistics-section mb-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-white p-4 rounded-lg shadow-sm border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">Total Tips</p>
              <p class="text-2xl font-bold text-gray-800">
                {{ statistics.total }}
              </p>
            </div>
            <i class="pi pi-lightbulb text-blue-500 text-2xl"></i>
          </div>
        </div>
        <div class="bg-white p-4 rounded-lg shadow-sm border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">Active Tips</p>
              <p class="text-2xl font-bold text-green-600">
                {{ statistics.active }}
              </p>
            </div>
            <i class="pi pi-check-circle text-green-500 text-2xl"></i>
          </div>
        </div>
        <div class="bg-white p-4 rounded-lg shadow-sm border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">Inactive Tips</p>
              <p class="text-2xl font-bold text-orange-600">
                {{ statistics.inactive }}
              </p>
            </div>
            <i class="pi pi-pause-circle text-orange-500 text-2xl"></i>
          </div>
        </div>
        <div class="bg-white p-4 rounded-lg shadow-sm border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">Dimensions</p>
              <p class="text-2xl font-bold text-purple-600">
                {{ Object.keys(statistics.byDimension).length }}
              </p>
            </div>
            <i class="pi pi-tags text-purple-500 text-2xl"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="filters-section mb-6">
      <DevTipsFilters
        :filters="filters"
        @update-filters="handleUpdateFilters"
        @clear-filters="handleClearFilters"
      />
    </div>

    <!-- Bulk Actions -->
    <div v-if="hasSelectedTips" class="bulk-actions-section mb-4">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div class="flex items-center gap-2">
            <i class="pi pi-info-circle text-blue-600"></i>
            <span class="text-blue-800 font-medium">
              {{ selectedTipsCount }} tip(s) selected
            </span>
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              label="Activate All"
              icon="pi pi-check"
              class="p-button-success p-button-sm"
              @click="handleBulkToggleStatus(true)"
            />
            <Button
              label="Deactivate All"
              icon="pi pi-pause"
              class="p-button-warning p-button-sm"
              @click="handleBulkToggleStatus(false)"
            />
            <Button
              label="Delete Selected"
              icon="pi pi-trash"
              class="p-button-danger p-button-sm"
              @click="handleBulkDelete"
            />
            <Button
              label="Clear Selection"
              icon="pi pi-times"
              class="p-button-text p-button-sm"
              @click="clearSelection"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Tips List -->
    <div class="tips-list-section">
      <!-- Dimension Status Summary -->
      <div v-if="tips.length > 0" class="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4 text-sm">
            <span class="text-gray-600">
              <i class="pi pi-list mr-1"></i>
              Total Tips: <strong>{{ tips.length }}</strong>
            </span>
            <span class="text-green-600">
              <i class="pi pi-check-circle mr-1"></i>
              Valid Dimensions: <strong>{{ tipsWithValidDimensions }}</strong>
            </span>
            <span class="text-orange-600">
              <i class="pi pi-exclamation-triangle mr-1"></i>
              Invalid Dimensions: <strong>{{ tipsWithInvalidDimensions }}</strong>
            </span>
            <span class="text-red-600">
              <i class="pi pi-times-circle mr-1"></i>
              No Dimension: <strong>{{ tipsWithoutDimensions }}</strong>
            </span>
          </div>
          <div v-if="tipsWithInvalidDimensions > 0 || tipsWithoutDimensions > 0"
             class="text-xs text-orange-600 bg-orange-100 px-2
              py-1 rounded border border-orange-300">
            ⚠️ {{ tipsWithInvalidDimensions + tipsWithoutDimensions }} tips need attention
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="flex justify-center items-center py-8">
        <ProgressSpinner />
      </div>

      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div class="flex items-center gap-2">
          <i class="pi pi-exclamation-triangle text-red-600"></i>
          <span class="text-red-800">{{ error }}</span>
        </div>
      </div>

      <div v-else-if="tips.length === 0" class="text-center py-8">
        <i class="pi pi-lightbulb text-gray-400 text-6xl mb-4"></i>
        <h3 class="text-xl font-semibold text-gray-600 mb-2">No Development Tips Found</h3>
        <p class="text-gray-500 mb-4">
          Create your first development tip to get started
        </p>
        <Button
          label="Create First Tip"
          icon="pi pi-plus"
          class="p-button-primary"
          @click="openCreateDialog"
        />
      </div>

      <div v-else class="space-y-4">
        <DevTipCard
          v-for="tip in tips"
          :key="tip._id"
          :tip="tip"
          :is-selected="selectedTips.includes(tip._id)"
          @select="handleTipSelection"
          @edit="handleEditTip"
          @toggle-status="handleToggleStatus"
          @delete="handleDeleteTip"
        />
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1" class="pagination-section mt-6">
      <Paginator
        :value="pagination.page"
        :rows="pagination.limit"
        :total-records="pagination.total"
        :rows-per-page-options="[5, 10, 20, 50]"
        @page="handlePageChange"
      />
    </div>

    <!-- Create/Edit Dialog -->
    <DevTipDialog
      v-model:visible="showTipDialog"
      :tip="editingTip"
      :is-editing="!!editingTip"
      @save="handleSaveTip"
      @cancel="closeTipDialog"
    />

    <!-- Bulk Import Dialog -->
    <BulkImportDialog
      v-model:visible="showBulkImportDialog"
      @import="handleBulkImport"
      @cancel="closeBulkImportDialog"
    />

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Paginator from 'primevue/paginator';
import ConfirmDialog from 'primevue/confirmdialog';

import DevTipsFilters from './components/DevTipsFilters.vue';
import DevTipCard from './components/DevTipCard.vue';
import DevTipDialog from './components/DevTipDialog.vue';
import BulkImportDialog from './components/BulkImportDialog.vue';

export default {
  name: 'DevTipsManagerComponent',
  components: {
    Button,
    ProgressSpinner,
    Paginator,
    ConfirmDialog,
    DevTipsFilters,
    DevTipCard,
    DevTipDialog,
    BulkImportDialog,
  },
  data() {
    return {
      showTipDialog: false,
      showBulkImportDialog: false,
      editingTip: null,
    };
  },
  computed: {
    ...mapState('devTips', [
      'tips',
      'loading',
      'error',
      'pagination',
      'filters',
      'statistics',
      'selectedTips',
    ]),
    ...mapGetters('devTips', [
      'isLoading',
      'hasSelectedTips',
      'selectedTipsCount',
    ]),
    ...mapGetters('dimensions', ['getAllDimensions']),

    // Dimension status counts
    tipsWithValidDimensions() {
      return this.tips.filter((tip) => this.isValidDimension(tip.dimensionValue)).length;
    },

    tipsWithInvalidDimensions() {
      return this.tips.filter((tip) => tip.dimensionValue && tip.dimensionValue.trim() &&
         !this.isValidDimension(tip.dimensionValue)).length;
    },

    tipsWithoutDimensions() {
      return this.tips.filter((tip) => !tip.dimensionValue || !tip.dimensionValue.trim()).length;
    },
  },
  async mounted() {
    await this.initializeData();
  },
  methods: {
    ...mapActions('devTips', [
      'loadTips',
      'loadAllTips',
      'createTip',
      'updateTip',
      'deleteTip',
      'toggleTipStatus',
      'bulkImport',
      'bulkDelete',
      'loadStatistics',
      'updateFilters',
      'clearFilters',
      'TOGGLE_TIP_SELECTION',
      'CLEAR_SELECTED_TIPS',
    ]),

    async initializeData() {
      try {
        // Check for dimension filter in URL query
        const dimensionFilter = this.$route.query.dimension;

        if (dimensionFilter) {
          // Apply dimension filter from URL
          await this.updateFilters({ dimensionValue: dimensionFilter });
        } else {
          // Load all tips using pagination to bypass backend limit of 100
          await this.loadTips();
        }

        await this.loadStatistics();
      } catch (error) {
        console.error('Failed to initialize DevTips manager:', error);
      }
    },

    // Filter handling
    async handleUpdateFilters(newFilters) {
      await this.updateFilters(newFilters);
    },

    async handleClearFilters() {
      await this.clearFilters();
    },

    async handleLoadAllTips() {
      try {
        await this.loadAllTips();

        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: `Loaded ${this.tips.length} development tips`,
          life: 3000,
        });
      } catch (error) {
        console.error('Failed to load all tips:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to load all tips: ${error.message}`,
          life: 5000,
        });
      }
    },

    // Pagination handling
    async handlePageChange(event) {
      await this.loadTips({
        page: event.page + 1,
        limit: event.rows,
      });
    },

    // Tip selection handling
    handleTipSelection(tipId) {
      this.$store.commit('devTips/TOGGLE_TIP_SELECTION', tipId);
    },

    clearSelection() {
      this.$store.commit('devTips/CLEAR_SELECTED_TIPS');
    },

    // Dimension validation
    isValidDimension(dimensionValue) {
      if (!dimensionValue || !dimensionValue.trim()) return false;

      const dimensions = this.getAllDimensions;
      if (!dimensions || dimensions.length === 0) return false;

      return dimensions.some((dim) => dim.value === dimensionValue);
    },

    // Dialog handling
    openCreateDialog() {
      this.editingTip = null;
      this.showTipDialog = true;
    },

    openBulkImportDialog() {
      this.showBulkImportDialog = true;
    },

    closeTipDialog() {
      this.showTipDialog = false;
      this.editingTip = null;
    },

    closeBulkImportDialog() {
      this.showBulkImportDialog = false;
    },

    // Tip CRUD operations
    handleEditTip(tip) {
      this.editingTip = { ...tip };
      this.showTipDialog = true;
    },

    async handleSaveTip(tipData) {
      try {
        if (this.editingTip) {
          await this.updateTip({
            id: this.editingTip._id,
            tipData,
          });
        } else {
          await this.createTip(tipData);
        }
        // Refresh statistics after creating/updating a tip
        await this.loadStatistics();
        this.closeTipDialog();
      } catch (error) {
        console.error('Failed to save tip:', error);
      }
    },

    async handleToggleStatus(tip) {
      try {
        await this.toggleTipStatus({
          id: tip._id,
          active: !tip.active,
        });
        // Refresh statistics after toggling tip status
        await this.loadStatistics();
      } catch (error) {
        console.error('Failed to toggle tip status:', error);
      }
    },

    async handleDeleteTip(tip) {
      this.$confirm.require({
        message: `Are you sure you want to delete the tip "${tip.title.en}"?`,
        header: 'Delete Development Tip',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await this.deleteTip(tip._id);
            // Refresh statistics after deleting a tip
            await this.loadStatistics();
          } catch (error) {
            console.error('Failed to delete tip:', error);
          }
        },
      });
    },

    // Bulk operations
    async handleBulkToggleStatus(active) {
      try {
        await this.$store.dispatch('devTips/bulkToggleStatus', {
          tipIds: this.selectedTips,
          active,
        });
        // Refresh statistics after bulk toggle status
        await this.loadStatistics();
        this.clearSelection();
      } catch (error) {
        console.error('Failed to bulk toggle status:', error);
      }
    },

    async handleBulkDelete() {
      this.$confirm.require({
        message: `Are you sure you want to delete ${this.selectedTipsCount} selected tip(s)?`,
        header: 'Bulk Delete Development Tips',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await this.bulkDelete(this.selectedTips);
            // Refresh statistics after bulk delete
            await this.loadStatistics();
          } catch (error) {
            console.error('Failed to bulk delete tips:', error);
          }
        },
      });
    },

    async handleBulkImport(bulkData) {
      try {
        await this.bulkImport(bulkData);
        this.closeBulkImportDialog();

        // Explicitly reload data to update the view
        await this.loadTips();
        await this.loadStatistics();
      } catch (error) {
        console.error('Failed to bulk import tips:', error);
      }
    },
  },
};
</script>

<style scoped>
.dev-tips-manager {
  padding: 1rem;
}

@media (max-width: 768px) {
  .dev-tips-manager {
    padding: 0.5rem;
  }
}
</style>
